<template>
  <section class="invoice iv-root">
    <LoadingSpinner v-if="loading" />
    <div v-else class="iv-wrap mx-2">
      <header
        class="iv-header d-flex justify-content-between align-items-start flex-wrap mb-3"
      >
        <div class="mb-2">
          <h1 class="iv-title mb-1">{{ $t('lang.dashboard.appointment_tracking') }}</h1>
          <p class="iv-subtitle mb-0">
            {{ $t('lang.dashboard.subtittle_dashboard_billing') }}
          </p>
        </div>
        <button type="button" class="btn iv-btn-export" @click="download">
          <span class="iv-btn-export-icon" aria-hidden="true">⭳</span>
          {{ $t('lang.dashboard.export_csv') }}
        </button>
      </header> 

      <p v-if="!hasAnyData" class="text-muted iv-empty">
        {{ $t('lang.dashboard.no_data_billing') }}
      </p>

      <div v-else class="iv-scroll-outer">
        <div class="iv-scroll-inner">
          <nav class="iv-year-tabs d-flex flex-wrap mb-3" aria-label="Années">
            <button
              v-for="y in yearKeys"
              :key="y"
              type="button"
              class="iv-year-tab"
              :class="{ 'iv-year-tab--active': String(selectedYear) === String(y) }"
              @click="selectedYear = y"
            >
              {{ y }}
            </button>
          </nav>

          <div
            v-for="month in monthsForSelectedYear"
            :key="String(selectedYear) + '-' + month"
            class="iv-month-card mb-3"
          >
            <div
              class="iv-month-head d-flex flex-wrap align-items-center justify-content-between"
              role="button"
              tabindex="0"
              @click="toggleMonth(selectedYear, month)"
              @keydown.enter.prevent="toggleMonth(selectedYear, month)"
              @keydown.space.prevent="toggleMonth(selectedYear, month)"
            >
              <div class="d-flex align-items-center flex-wrap iv-month-head-left">
                <span
                  class="iv-chevron mr-2"
                  :class="{ 'iv-chevron--open': isMonthOpen(selectedYear, month) }"
                  aria-hidden="true"
                />
                <span class="iv-month-label font-weight-bold">
                  {{ getLabelMonthFr(month) }} {{ selectedYear }}
                </span>
                <div class="iv-month-badges d-flex flex-wrap align-items-center ml-2 mt-1 mt-sm-0">
                  <span
                    v-for="pill in monthSummaryPills(selectedYear, month)"
                    :key="pill.kind"
                    class="iv-pill"
                    :class="pill.pillClass"
                    :title="pill.title"
                  >
                    <i
                      v-if="pill.iconClass"
                      :class="[pill.iconClass, 'iv-pill-icon']"
                      aria-hidden="true"
                    />
                    <span
                      v-else
                      class="iv-pill-icon"
                      :class="pill.glyphClass"
                      aria-hidden="true"
                    >{{ pill.glyph }}</span>
                    {{ pill.count }}
                  </span>
                </div>
              </div>
              <div class="iv-month-head-right text-right mt-2 mt-md-0">
                <div class="iv-entries text-muted small">
                  {{ monthCounts(selectedYear, month).entries }} entrées
                </div>
                <template v-if="hasPermissions('manageBilling')">
                  <div class="iv-net-label text-muted text-uppercase small">
                    {{ $t('lang.dashboard.net_month') }}
                  </div>
                  <div class="iv-net-amount font-weight-bold">
                    {{ formatMoneyFr(getTotal(selectedYear, month)) }} € TTC
                  </div>
                </template>
              </div>
            </div>

            <transition name="iv-collapse">
              <div
                v-show="isMonthOpen(selectedYear, month)"
                :id="'iv-collapse-' + selectedYear + '-' + month"
                class="iv-month-body"
              >
                <div
                  v-for="(opp, index) in getOrderedListMonth(
                    opportunities[selectedYear][month]
                  )"
                  :key="'row-' + selectedYear + '-' + month + '-' + index"
                  class="iv-row d-flex flex-wrap align-items-stretch"
                  :class="{ 'iv-row--credit': isCreditOpp(opp) }"
                >
                  <div
                    class="iv-row-bar"
                    :style="{ backgroundColor: getRowBarColor(opp) }"
                  />

                  <div
                    class="iv-row-main d-flex flex-grow-1 flex-nowrap align-items-center min-w-0"
                  >
                    <div class="iv-row-avatar-wrap flex-shrink-0">
                      <div
                        v-if="isCreditOpp(opp)"
                        class="iv-avatar iv-avatar--credit"
                        aria-hidden="true"
                      >
                        <span class="iv-avatar-credit-icon">↻</span>
                      </div>
                      <img
                        v-else
                        class="iv-avatar"
                        :src="avatarUrl(opp)"
                        alt=""
                        @error="onAvatarImgError"
                      />
                    </div>

                    <div class="iv-row-info flex-grow-1 min-w-0">
                      <div class="d-flex flex-wrap align-items-center iv-row-title-line">
                        <template>
                          <a
                            v-if="opp.link_id != null && opp.link_id != 0"
                            class="iv-row-title iv-row-title--link"
                            target="_blank"
                            rel="noopener"
                            :href="'/opportunities/' + opp.link_id"
                          >
                            {{ opp.business_name }}
                          </a>
                          <span v-else class="iv-row-title">{{ opp.business_name }}</span>
                        </template>

                        <span
                          v-if="opp.claim_status_key"
                          class="iv-claim-badge ml-2 mt-1"
                          :class="claimBadgeClass(opp.claim_status_key)"
                        >
                          <span class="iv-claim-dot" aria-hidden="true" />
                          {{ opp.claim_label_fr }}
                        </span>
                      </div>

                      <div
                        v-if="!isCreditOpp(opp)"
                        class="iv-meta d-flex align-items-center text-muted small mt-1"
                      >
                        <span v-if="opp.prospect_name" class="iv-meta-item">
                          <i class="icon-user iv-meta-ico" aria-hidden="true"></i>
                          {{ opp.prospect_name }}
                        </span>
                        <span
                          v-if="opp.real_event_date"
                          class="iv-meta-item"
                        >
                          <i class="icon-file-text-o iv-meta-ico" aria-hidden="true"></i>
                          {{ $t('lang.dashboard.billed_on') }}
                          {{ formatDateFr(opp.event_date) }}
                        </span>
                        <span
                          v-if="opp.real_event_date || opp.event_date"
                          class="iv-meta-item"
                        >
                          <i class="icon-calendar iv-meta-ico" aria-hidden="true"></i>
                          RDV {{ formatDateFr(opp.real_event_date || opp.event_date) }}
                        </span>
                        <span>
                          <i class="icon-user iv-meta-ico" aria-hidden="true"></i>
                          {{ opp.owner_name }}
                        </span>
                        <span
                          v-if="isAbsentHeldSurcharge(opp)"
                          class="iv-meta-item"
                        >
                          <i class="icon-tag iv-meta-ico" aria-hidden="true"></i>
                          {{ $t('lang.dashboard.absent_held_surcharge', absentHeldSurchargeAmounts(opp)) }}
                        </span>
                      </div>

                      <div
                        v-else
                        class="iv-meta iv-meta--credit d-flex align-items-center text-muted small mt-1"
                      >
                        <span
                          v-if="opp.real_event_date || opp.event_date"
                          class="iv-meta-item"
                        >
                          <i class="icon-calendar iv-meta-ico" aria-hidden="true"></i>
                          {{ formatDateFr(opp.real_event_date || opp.event_date) }}
                        </span>
                        <span v-if="opp.credit_reason" class="iv-meta-item iv-meta-item--grow">
                          <i class="icon-tag iv-meta-ico" aria-hidden="true"></i>
                          {{ $t('lang.dashboard.reason') }} : {{ opp.credit_reason }}
                        </span>
                      </div>
                    </div>

                    <div
                      class="iv-row-badges d-flex flex-row flex-nowrap align-items-center flex-shrink-1 min-w-0"
                    >
                      <span
                        class="iv-status-pill"
                        :class="meetingStatusPillClass(opp)"
                      >
                        <i
                          v-if="meetingStatusIconIsMaterial(opp)"
                          :class="[meetingStatusIconClass(opp), 'iv-status-pill-ico']"
                          aria-hidden="true"
                        />
                        <span
                          v-else
                          class="iv-status-pill-ico"
                          aria-hidden="true"
                        >{{ meetingStatusIconGlyph(opp) }}</span>
                        {{ meetingStatusLabelFr(opp) }}
                      </span>
                      <a
                        v-if="!isCreditOpp(opp) && opp.target_id"
                        class="iv-target-pill text-decoration-none"
                        target="_blank"
                        rel="noopener"
                        :href="'/orders/' + opp.target_id"
                      >
                        {{ opp.target_name }}
                      </a>
                      <span
                        v-else-if="!isCreditOpp(opp) && opp.target_name"
                        class="iv-target-pill iv-target-pill--static"
                      >{{ opp.target_name }}</span>
                    </div>


                    <div
                      v-if="hasPermissions('manageBilling')"
                      class="iv-row-money text-right flex-shrink-0"
                    >
                      <div
                        v-if="showsFrenchVat"
                        class="iv-money-ht text-muted small"
                      >
                        {{ amountExVatFormatted(opp) }} € HT
                      </div>
                      <div
                        class="iv-money-ttc font-weight-bold"
                        :class="{ 'iv-money-ttc--credit': isCreditOpp(opp) }"
                      >
                        {{ amountWithVatFormatted(opp) }} €
                        <template v-if="showsFrenchVat">
                          TTC</template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { billingsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

const AVATAR_FALLBACK =
  "data:image/svg+xml;base64,PHN2ZyBmaWxsPSJub25lIiBoZWlnaHQ9IjMyIiB3aWR0aD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHJ4PSIxNiIgZmlsbD0iIzkwY2FmOSIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iMTIiIHI9IjUiIGZpbGw9IndoaXRlIi8+PHBhdGggZD0iTTE2IDE4Yy02LjYgMC0xMiA0LjItMTIgOXYxYzAgMS4xLjkgMiAyIDJoMjhjMS4xIDAgMi0uOSAyLTJ2LTFjMC00LjgtNS40LTktMTItOXoiIGZpbGw9IndoaXRlIi8+PC9zdmc+";

/** Ordre d’affichage des compteurs dans l’en-tête du mois */
const MONTH_SUMMARY_PILLS = [
  { kind: "validated", pillClass: "iv-pill--ok", glyph: "✓" },
  { kind: "next_pay", pillClass: "iv-pill--billable", iconClass: "icon-money" },
  { kind: "planned", pillClass: "iv-pill--planned", iconClass: "icon-calendar" },
  { kind: "absent", pillClass: "iv-pill--warn", glyph: "✕" },
  { kind: "signaled", pillClass: "iv-pill--danger", glyph: "⚑" },
  { kind: "pay_declined", pillClass: "iv-pill--declined", iconClass: "icon-warning" },
  { kind: "pay_pending", pillClass: "iv-pill--pending", iconClass: "icon-watch_later" },
  { kind: "credit", pillClass: "iv-pill--credit", glyph: "↻", glyphClass: "iv-pill-icon--spin" },
];

export default {
  components: {
    LoadingSpinner,
  },
  props: {
    userId: {
      type: String,
      default: null,
    },
    targetId: {
      type: String,
      default: "0",
    },
  },
  data() {
    return {
      totalList: [],
      loading: true,
      opportunities: {},
      active: {},
      refused: {},
      totalexported: {},
      absent: {},
      totalexp: 0,
      billingProfile: {},
      selectedYear: null,
      expandedMonths: {},
      avatarFallback: AVATAR_FALLBACK,
    };
  },
  computed: {
    resolvedUserId() {
      if (this.userId != null && this.userId !== "") {
        return this.userId;
      }
      const user = this.$store.state.authentication.user;
      if (user && user.id != null && user.id !== "") {
        return String(user.id);
      }
      return null;
    },
    yearKeys() {
      return Object.keys(this.opportunities);
    },
    monthsForSelectedYear() {
      if (this.selectedYear == null || this.selectedYear === "") return [];
      const y = this.opportunities[this.selectedYear];
      if (!y) return [];
      return Object.keys(y);
    },
    hasAnyData() {
      return this.yearKeys.length > 0;
    },
    /** Taux TVA issu du profil (ex. "20.00") — 0 si absent ou invalide */
    billingVatPercent() {
      const v = parseFloat(this.billingProfile.country_vat);
      if (!Number.isFinite(v) || v <= 0) return 0;
      return v;
    },
    /** France (73) avec TVA renseignée : HT = amount API, TTC = amount + TVA */
    showsFrenchVat() {
      const id = this.billingProfile.country_id;
      const isFr = id == 73 || String(id) === "73";
      return isFr && this.billingVatPercent > 0;
    },
  },
  watch: {
    selectedYear(newYear, oldYear) {
      if (newYear === oldYear) return;
      this.expandedMonths = {};
    },
  },
  created() {
    this.getBillingProfile();
    this.getData();
  },
  methods: {
    hasPermissions(perm) {
      return this.$store.getters["authentication/hasPermissions"](perm);
    },
    isCreditOpp(opp) {
      return opp.is_credit == 1 || opp.is_credit === "1";
    },
    isValidatedMeeting(opp) {
      if (this.isCreditOpp(opp)) return false;
      const id = String(opp.link_status_reason_id);
      if (id === "35" || id === "34") return false;
      if (
        opp.billing_status === "next_pay" ||
        opp.billing_status === "pay_declined" ||
        opp.billing_status === "pay_pending"
      ) {
        return false;
      }
      return opp.real_event_date != null || opp.billing_status === "pay_paid";
    },
    meetingStatusLabels() {
      return {
        credit: "CRÉDIT",
        absent: "ABSENT",
        signaled: "SIGNALÉ",
        validated: "VALIDÉ",
        next_pay: "FACTURABLE",
        pay_declined: "PAIEMENT REFUSÉ",
        pay_pending: "PAIEMENT EN ATTENTE",
        planned: "PRÉVU",
      };
    },
    monthCounts(year, month) {
      const list = (this.opportunities[year] && this.opportunities[year][month]) || [];
      const counts = {
        credit: 0,
        absent: 0,
        signaled: 0,
        validated: 0,
        next_pay: 0,
        pay_declined: 0,
        pay_pending: 0,
        planned: 0,
        entries: 0,
      };
      list.forEach((opp) => {
        const kind = this.meetingStatusKind(opp);
        if (counts[kind] !== undefined) counts[kind]++;
      });
      counts.entries =
        this.active[year] && this.active[year][month]
          ? this.active[year][month].total
          : 0;
      return counts;
    },
    monthSummaryPills(year, month) {
      const counts = this.monthCounts(year, month);
      const labels = this.meetingStatusLabels();
      return MONTH_SUMMARY_PILLS.filter((cfg) => counts[cfg.kind] > 0).map(
        (cfg) => ({
          kind: cfg.kind,
          count: counts[cfg.kind],
          pillClass: cfg.pillClass,
          iconClass: cfg.iconClass || null,
          glyph: cfg.glyph || null,
          glyphClass: cfg.glyphClass || null,
          title: `${labels[cfg.kind]} : ${counts[cfg.kind]}`,
        })
      );
    },
    toggleMonth(year, month) {
      const key = `${year}-${month}`;
      this.$set(this.expandedMonths, key, !this.expandedMonths[key]);
    },
    isMonthOpen(year, month) {
      return !!this.expandedMonths[`${year}-${month}`];
    },
    resolveDefaultYear(yearKeys) {
      if (!yearKeys || yearKeys.length === 0) return null;
      const current = String(new Date().getFullYear());
      if (yearKeys.some((y) => String(y) === current)) return current;
      return yearKeys[0];
    },
    getBillingProfile() {
      billingsService.getBillingProfile(this.resolvedUserId).then((result) => {
        if (result) {
          this.billingProfile = result;
        } else {
          this.billingProfile = {
            name: "",
            legal_representative: "",
            address: "",
            country_id: 73,
            contact_name: "",
            contact_email: "",
          };
        }
      });
    },
    getData() {
      this.loading = true;
      billingsService
        .getDashboard(this.resolvedUserId, this.targetId)
        .then((result) => {
          let list = result && Array.isArray(result.list) ? result.list : [];
          if (this.targetId != "0") {
            list = list.filter((el) => el.target_id == this.targetId);
          }
          this.totalList = list;

          const opportunities = {};
          const active = {};
          const refused = {};
          const absent = {};

          list.forEach((item) => {
            if (item.claim_status) {
              item.claim_status_key = item.claim_status;
              switch (item.claim_status) {
                case "new_claim":
                  item.claim_label_fr = "Réclamation en cours";
                  break;
                case "on_going":
                  item.claim_label_fr = "Réclamation approuvée";
                  break;
                case "closed":
                  item.claim_label_fr = "Réclamation refusée";
                  break;
                default:
                  item.claim_label_fr = String(item.claim_status);
                  break;
              }
            } else {
              item.claim_status_key = null;
              item.claim_label_fr = null;
            }

            const year = item.event_year;
            const month = item.event_month;

            if (opportunities[year] == undefined) {
              opportunities[year] = {};
            }
            if (opportunities[year][month] == undefined) {
              opportunities[year][month] = [];
            }
            opportunities[year][month].push(item);

            if (item.link_status_reason_id == 35) {
              if (absent[year] == undefined) {
                absent[year] = {};
              }
              if (absent[year][month] == undefined) {
                absent[year][month] = { total: 0 };
              }
              absent[year][month].total++;
            } else if (item.link_status_reason_id == 34) {
              if (refused[year] == undefined) {
                refused[year] = {};
              }
              if (refused[year][month] == undefined) {
                refused[year][month] = { total: 0 };
              }
              refused[year][month].total++;
            } else {
              if (active[year] == undefined) {
                active[year] = {};
              }
              if (active[year][month] == undefined) {
                active[year][month] = {
                  total: 0,
                  totalAmount: 0,
                };
              }

              const amt = parseFloat(item.amount);
              const amtNum = Number.isFinite(amt) ? amt : 0;
              if (item.is_credit == 0) {
                active[year][month].total++;
              }
              active[year][month].totalAmount += amtNum;
            }
          });

          this.opportunities = opportunities;
          this.active = active;
          this.refused = refused;
          this.absent = absent;

          const yk = Object.keys(opportunities);
          if (
            this.selectedYear == null ||
            yk.indexOf(String(this.selectedYear)) === -1
          ) {
            this.selectedYear = this.resolveDefaultYear(yk);
          }
        })
        .catch(() => {
          this.totalList = [];
          this.opportunities = {};
          this.active = {};
          this.refused = {};
          this.absent = {};
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getTotal(year, month) {
      if (
        this.active[year] === undefined ||
        this.active[year][month] === undefined
      ) {
        return parseFloat(0).toFixed(2);
      }
      // Los créditos (importes negativos) son HT: el IVA se aplica al neto HT del mes, igual que en la factura
      const total = this.active[year][month].totalAmount;
      const vatRate = this.showsFrenchVat ? this.billingVatPercent : 0;
      const totalWithVat = total * (1 + vatRate / 100);
      return parseFloat(totalWithVat).toFixed(2);
    },
    getLabelMonthFr(month) {
      const m = parseInt(month, 10);
      const names = [
        "",
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ];
      return names[m] || String(month);
    },
    formatDateFr(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },
    formatMoneyFr(numStr) {
      const n = parseFloat(String(numStr));
      if (Number.isNaN(n)) return String(numStr);
      return n.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    billingStatusLabelFr(opp) {
      if (opp.billing_status === "next_pay") return "À encaisser";
      if (opp.billing_status === "pay_declined") return "Paiement refusé";
      if (opp.billing_status === "pay_pending") return "Paiement en attente";
      if (opp.real_event_date != null || opp.billing_status === "pay_paid") {
        return "Encaissé";
      }
      return "Facturable";
    },
    meetingStatusKind(opp) {
      if (this.isCreditOpp(opp)) return "credit";
      const id = String(opp.link_status_reason_id);
      if (id === "35") return "absent";
      if (id === "34") return "signaled";
      if (this.isValidatedMeeting(opp)) return "validated";
      if (opp.billing_status === "next_pay") return "next_pay";
      if (opp.billing_status === "pay_declined") return "pay_declined";
      if (opp.billing_status === "pay_pending") return "pay_pending";
      return "planned";
    },
    getRowBarColor(opp) {
      const k = this.meetingStatusKind(opp);
      const byKind = {
        credit: "#7c3aed",
        absent: "#f59e0b",
        signaled: "#ef4444",
        validated: "#16a34a",
        next_pay: "#64748b",
        pay_declined: "#dc2626",
        pay_pending: "#ea580c",
        planned: "#94a3b8",
      };
      return byKind[k] || "#94a3b8";
    },
    meetingStatusLabelFr(opp) {
      const labels = this.meetingStatusLabels();
      return labels[this.meetingStatusKind(opp)] || "PRÉVU";
    },
    meetingStatusIconIsMaterial(opp) {
      return ["planned", "next_pay", "pay_declined", "pay_pending"].includes(
        this.meetingStatusKind(opp)
      );
    },
    meetingStatusIconClass(opp) {
      const icons = {
        planned: "icon-calendar",
        next_pay: "icon-money",
        pay_declined: "icon-warning",
        pay_pending: "icon-watch_later",
      };
      return icons[this.meetingStatusKind(opp)] || "icon-calendar";
    },
    meetingStatusIconGlyph(opp) {
      const k = this.meetingStatusKind(opp);
      if (k === "credit") return "↻";
      if (k === "absent") return "✕";
      if (k === "signaled") return "⚑";
      if (k === "validated") return "✓";
      return "";
    },
    meetingStatusPillClass(opp) {
      const classes = {
        credit: "iv-status-pill--credit",
        absent: "iv-status-pill--absent",
        signaled: "iv-status-pill--signaled",
        validated: "iv-status-pill--ok",
        next_pay: "iv-status-pill--billable",
        pay_declined: "iv-status-pill--declined",
        pay_pending: "iv-status-pill--pending",
        planned: "iv-status-pill--planned",
      };
      return classes[this.meetingStatusKind(opp)] || "iv-status-pill--planned";
    },
    claimBadgeClass(key) {
      if (key === "new_claim") return "iv-claim--new";
      if (key === "on_going") return "iv-claim--approved";
      if (key === "closed") return "iv-claim--rejected";
      return "";
    },
    avatarUrl(opp) {
      const pick = (v) => {
        if (v == null) return null;
        const s = String(v).trim();
        if (s === "" || s.toLowerCase() === "null") return null;
        return s;
      };
      return (
        pick(opp.business_logo) ||
        pick(opp.avatar) ||
        this.avatarFallback
      );
    },
    onAvatarImgError(e) {
      if (e && e.target) e.target.src = this.avatarFallback;
    },
    amountNumeric(opp) {
      const n = parseFloat(opp && opp.amount);
      return Number.isFinite(n) ? n : 0;
    },
    /** RDV déclaré absent puis tenu (motif 38) : facturé avec majoration 10 % (CGU art. 7.3) */
    isAbsentHeldSurcharge(opp) {
      return (
        opp.link_status_reason_id == 38 &&
        (opp.real_event_date != null || opp.billing_status === "pay_paid")
      );
    },
    absentHeldSurchargeAmounts(opp) {
      const total = this.amountNumeric(opp);
      return {
        base: this.formatMoneyFr(Math.round((total / 1.1) * 100) / 100),
        total: this.formatMoneyFr(total),
      };
    },
    amountExVatFormatted(opp) {
      return this.formatMoneyFr(this.amountNumeric(opp));
    },
    amountWithVatFormatted(opp) {
      const base = this.amountNumeric(opp);
      if (this.showsFrenchVat) {
        const ttc = base * (1 + this.billingVatPercent / 100);
        return this.formatMoneyFr(ttc);
      }
      return this.formatMoneyFr(base);
    },
    download() {
      const headers = [
        "Statut",
        "Responsable",
        "Rendez-vous / entreprise",
        "Prospect",
        "Email du prospect",
        "Date de la réunion",
        "Date de la facturation",
        "Campagne",
        "Prix (€ HT)",
        "Prix (€ TTC)",
      ];

      const rows = this.totalList.map((item) => {
        const amt = parseFloat(item.amount);
        const base = Number.isFinite(amt) ? amt : 0;
        const ht = base.toFixed(2);
        const applyVat = this.showsFrenchVat;
        const ttc = applyVat
          ? (base * (1 + this.billingVatPercent / 100)).toFixed(2)
          : base.toFixed(2);
        return [
          this.exportStatusFr(item),
          item.owner_name,
          item.business_name,
          item.prospect_name,
          item.prospect_email,
          item.real_event_date,
          item.event_date,
          item.target_name,
          ht,
          ttc,
        ]
          .map((v) =>
            `"${String(v !== undefined && v !== null ? v : "").replace(/"/g, '""')}"`
          )
          .join(",");
      });

      const csv = `${headers.join(",")}\n${rows.join("\n")}`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "billing-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    exportStatusFr(item) {
      if (this.isCreditOpp(item)) return "Crédit";
      if (item.link_status_reason_id == 35) return "Absent";
      if (item.link_status_reason_id == 34) return "Signalé";
      return this.billingStatusLabelFr(item);
    },
    getOrderedListMonth(listMonth) {
      if (!listMonth || listMonth.length === 0) return [];

      const meetings = listMonth.filter((item) => item.is_credit == 0);
      const creditsWithLink = listMonth.filter(
        (item) =>
          item.is_credit == 1 && item.link_id != null && item.link_id != 0
      );
      const creditsWithoutLink = listMonth.filter(
        (item) =>
          item.is_credit == 1 && (item.link_id == null || item.link_id == 0)
      );

      const getDate = (item) => {
        const dateStr = item.real_event_date || item.event_date;
        return dateStr ? new Date(dateStr) : new Date(0);
      };

      meetings.sort((a, b) => getDate(a) - getDate(b));
      creditsWithoutLink.sort((a, b) => getDate(a) - getDate(b));

      const creditsByLinkId = {};
      creditsWithLink.forEach((credit) => {
        if (!creditsByLinkId[credit.link_id]) {
          creditsByLinkId[credit.link_id] = [];
        }
        creditsByLinkId[credit.link_id].push(credit);
      });

      const result = [];
      let meetingIndex = 0;
      let creditIndex = 0;

      while (meetingIndex < meetings.length || creditIndex < creditsWithoutLink.length) {
        if (meetingIndex >= meetings.length) {
          result.push(creditsWithoutLink[creditIndex]);
          creditIndex++;
        } else if (creditIndex >= creditsWithoutLink.length) {
          const meeting = meetings[meetingIndex];
          result.push(meeting);
          if (creditsByLinkId[meeting.link_id]) {
            result.push(...creditsByLinkId[meeting.link_id]);
          }
          meetingIndex++;
        } else {
          if (getDate(meetings[meetingIndex]) <= getDate(creditsWithoutLink[creditIndex])) {
            const meeting = meetings[meetingIndex];
            result.push(meeting);
            if (creditsByLinkId[meeting.link_id]) {
              result.push(...creditsByLinkId[meeting.link_id]);
            }
            meetingIndex++;
          } else {
            result.push(creditsWithoutLink[creditIndex]);
            creditIndex++;
          }
        }
      }

      const orphanCredits = creditsWithLink.filter(
        (credit) =>
          !meetings.some((m) => String(m.link_id) === String(credit.link_id))
      );
      orphanCredits.sort((a, b) => getDate(a) - getDate(b));
      result.push(...orphanCredits);

      return result;
    },
  },
};
</script>

<style scoped>
.iv-root {
  --iv-purple: #5054EB;
  --iv-purple-light: #ede9fe;
  --iv-navy: #5054EB;
  --iv-border: #e2e8f0;
}

.iv-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--iv-navy);
}

.iv-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  max-width: 36rem;
}

.iv-btn-export {
  background: var(--iv-purple);
  border: none;
  color: #fff;
  font-weight: 600;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.iv-btn-export:hover {
  background: #5054EB;
  color: #fff;
}

.iv-btn-export-icon {
  font-size: 1rem;
  line-height: 1;
}

.iv-scroll-outer {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.iv-scroll-inner {
  min-width: 1220px;
  padding-bottom: 0.5rem;
}

.iv-year-tabs {
  gap: 0.5rem;
}

.iv-year-tab {
  border: 1px solid var(--iv-border);
  background: #fff;
  color: #475569;
  font-weight: 600;
  padding: 0.4rem 1rem;
  border-radius: 0.35rem;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}

.iv-year-tab--active {
  background: var(--iv-purple);
  border-color: var(--iv-purple);
  color: #fff;
}

.iv-month-card {
  background: #fff;
  border: 1px solid var(--iv-border);
  border-radius: 0.65rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.iv-month-head {
  padding: 0.85rem 1rem;
  cursor: pointer;
  user-select: none;
  background: #fafafa;
  border-bottom: 1px solid transparent;
}

.iv-month-head:hover {
  background: #f4f4f5;
}

.iv-month-body {
  padding: 0.75rem 0.75rem 1rem;
  background: #f8fafc;
  border-top: 1px solid var(--iv-border);
}

.iv-chevron {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-right: 2px solid #64748b;
  border-bottom: 2px solid #64748b;
  transform: rotate(-45deg);
  transition: transform 0.2s ease;
  vertical-align: middle;
}

.iv-chevron--open {
  transform: rotate(45deg);
}

.iv-month-label {
  color: var(--iv-navy);
  font-size: 1rem;
}

.iv-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  margin-right: 0.25rem;
}

.iv-pill-icon {
  font-size: 0.7rem;
}

.iv-pill-icon--spin {
  font-size: 0.85rem;
}

.iv-pill--ok {
  background: #dcfce7;
  color: #166534;
}

.iv-pill--warn {
  background: #fef9c3;
  color: #854d0e;
}

.iv-pill--danger {
  background: #fee2e2;
  color: #991b1b;
}

.iv-pill--credit {
  background: var(--iv-purple-light);
  color: #5b21b6;
}

.iv-pill--planned {
  background: #e2e8f0;
  color: #475569;
}

.iv-pill--billable {
  background: #f1f5f9;
  color: #475569;
}

.iv-pill--declined {
  background: #fee2e2;
  color: #991b1b;
}

.iv-pill--pending {
  background: #ffedd5;
  color: #c2410c;
}

.iv-pill i.iv-pill-icon {
  font-size: 0.8rem;
}

.iv-net-label {
  letter-spacing: 0.04em;
  font-size: 0.65rem;
}

.iv-net-amount {
  font-size: 1.15rem;
  color: var(--iv-navy);
}

.iv-collapse-enter-active,
.iv-collapse-leave-active {
  transition:
    opacity 0.2s ease,
    max-height 0.25s ease;
  overflow: hidden;
}

.iv-collapse-enter,
.iv-collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

.iv-collapse-enter-to,
.iv-collapse-leave-from {
  max-height: 4000px;
}

.iv-row {
  position: relative;
  background: #fff;
  border: 1px solid var(--iv-border);
  border-radius: 0.5rem;
  margin-bottom: 0.6rem;
  min-height: 4.5rem;
  overflow-x: auto;
  overflow-y: visible;
}

.iv-row--credit {
  border-color: #ddd6fe;
}

.iv-row-bar {
  width: 4px;
  flex-shrink: 0;
  align-self: stretch;
}

.iv-row-main {
  padding: 0.65rem 0.75rem;
  gap: 0.75rem;
  flex: 1 1 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: visible;
}

.iv-row-avatar-wrap {
  padding-top: 0.1rem;
}

.iv-avatar {
  width: 44px;
  height: 44px;
  border-radius: 0.45rem;
  object-fit: cover;
  border: 1px solid var(--iv-border);
  background: #f1f5f9;
}

.iv-avatar--credit {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--iv-purple-light);
  border-color: #c4b5fd;
  color: #5b21b6;
  font-size: 1.25rem;
}

.iv-avatar-credit-icon {
  line-height: 1;
}

.iv-row-title {
  font-weight: 700;
  color: var(--iv-navy);
  font-size: 1rem;
}

.iv-row-title--link {
  color: var(--iv-navy);
  text-decoration: none;
}

.iv-row-title--link:hover {
  text-decoration: underline;
  color: var(--iv-purple);
}

.iv-claim-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.iv-claim-dot {
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 50%;
  margin-right: 0.35rem;
  background: currentColor;
}

.iv-claim--new {
  color: #1d4ed8;
  border-color: #93c5fd;
  background: #eff6ff;
}

.iv-claim--approved {
  color: #166534;
  border-color: #86efac;
  background: #f0fdf4;
}

.iv-claim--rejected {
  color: #991b1b;
  border-color: #fca5a5;
  background: #fef2f2;
}

.iv-meta-ico {
  margin-right: 0.35rem;
  opacity: 0.75;
  font-size: 0.85rem;
  vertical-align: text-bottom;
}

.iv-meta {
  flex-wrap: nowrap;
  gap: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
}

.iv-meta-item {
  flex-shrink: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.iv-meta-item--commande {
  flex-shrink: 1;
  min-width: 0;
}

.iv-commande-link {
  max-width: 22rem;
  vertical-align: bottom;
}

.iv-meta--credit {
  flex-wrap: nowrap;
  gap: 0.75rem;
  overflow-x: auto;
  min-width: 0;
}

.iv-meta-item--grow {
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.iv-row-badges {
  flex-wrap: nowrap;
  gap: 0.5rem;
  align-items: center;
  min-width: 10rem;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0.25rem 0.5rem;
}

.iv-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  text-transform: uppercase;
}

.iv-status-pill-ico {
  font-size: 0.75rem;
  line-height: 1;
}

.iv-status-pill i.iv-status-pill-ico {
  font-size: 0.85rem;
}

.iv-status-pill--ok {
  background: #dcfce7;
  color: #166534;
}

.iv-status-pill--absent {
  background: #fef9c3;
  color: #854d0e;
}

.iv-status-pill--signaled {
  background: #fee2e2;
  color: #991b1b;
}

.iv-status-pill--credit {
  background: var(--iv-purple-light);
  color: #5b21b6;
}

.iv-status-pill--planned {
  background: #e2e8f0;
  color: #475569;
}

.iv-status-pill--billable {
  background: #f1f5f9;
  color: #475569;
}

.iv-status-pill--declined {
  background: #fee2e2;
  color: #991b1b;
}

.iv-status-pill--pending {
  background: #ffedd5;
  color: #c2410c;
}

.iv-target-pill {
  display: inline-flex;
  align-items: center;
  max-width: 16rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  background: #fff;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iv-target-pill--static {
  cursor: default;
}

.iv-row-money {
  min-width: 7.5rem;
  padding: 0.25rem 0.5rem 0.25rem 0;
  margin-left: auto;
}

.iv-money-ttc {
  font-size: 1.05rem;
  color: var(--iv-navy);
}

.iv-money-ttc--credit {
  color: #5b21b6;
}

.min-w-0 {
  min-width: 0;
}

@media (max-width: 991px) {
  .iv-row-money {
    width: 100%;
    text-align: left !important;
    margin-top: 0.5rem;
    padding-left: 3.25rem;
  }
}
</style>
